import React, { useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router";
import { LuShieldCheck, LuGauge, LuHeart } from "react-icons/lu";
import { GlobalStateContext } from "../controller/GlobalState";
import { emailRegex, isValidPhone, companyServices, tech, workflowsteps, chooseus } from "../controller/global";
import { submitLead } from "../controller/leads";

const parent = companyServices.find((s) => s.key === "mobile_app_development");
const facetBlurb = "Mobile app performance and security: faster startup, smoother screens and smaller downloads, plus secure storage, certificate pinning and hardened APIs for iOS and Android apps.";

const FEATURE_ROWS = [
  { num: "01 / SPEED", Icon: LuGauge, title: "Optimized for speed, not just built to work.", copy: "We profile the app on real devices to find what slows it down, such as startup work, heavy images, unnecessary network calls or memory leaks, and fix the causes rather than the symptoms.", tags: ["React Native", "Flutter", "Optimized builds"] },
  { num: "02 / SECURITY", Icon: LuShieldCheck, title: "Secure by design, not patched after launch.", copy: "Security is built in: tokens in the device's secure storage, certificate pinning, obfuscation where it matters, and APIs that check every request, following the OWASP Mobile Top 10.", tags: ["Secure data handling", "Safe API calls", "Reliable auth"] },
  { num: "03 / EXPERIENCE", Icon: LuHeart, title: "Seamless experiences users stick with.", copy: "Crash reporting and performance monitoring stay on after launch, so issues in the field are fixed before they show up as one-star reviews.", tags: ["Feature-rich", "User-friendly", "Engagement-focused"] },
];

const PROCESS_STEPS = workflowsteps.map((w) => ({ n: w.number, t: w.title, d: w.description }));

const mobileTools = tech.find((t) => t.key === "mobile-app")?.children.map((c) => c.name) ?? [];
const backEndTools = tech.find((t) => t.key === "back-end")?.children.map((c) => c.name) ?? [];

const FACTS = [
  { a: "MSME", b: "Registered IT company" }, { a: "Delhi NCR", b: "Headquarters" },
  { a: "7", b: "Stage delivery process" }, { a: "11", b: "Core services offered" },
];

const RELATED = [{ title: "Native & Cross-Platform Apps", path: "/native-cross-platform-apps" }];

const FAQS = [
  { q: "Can you audit an existing app's performance or security?", a: "Yes — we regularly audit existing apps for performance bottlenecks and security gaps, without requiring a full rebuild." },
  { q: "What does \"secure\" actually mean for a mobile app?", a: "It covers things like secure data storage, safe API communication, and reliable authentication — the fundamentals that most security issues in mobile apps trace back to." },
  { q: "Why is my mobile app slow?", a: "Common causes are too much work at startup, unoptimized images, too many network requests and memory leaks. Profiling on real devices shows which applies to your app." },
  { q: "How do you test mobile app security?", a: "We review the code and configuration against the OWASP Mobile Top 10, test how data is stored and sent, and check the APIs the app calls." },
  { q: "Can you improve an app built by another team?", a: "Yes. We audit it first, then fix the highest-impact performance and security issues without a full rebuild." },
  { q: "What engagement models do you offer?", a: "We work Fixed-Scope, Dedicated Team, or MVP Sprint — the same process and team stay accountable from kickoff to launch regardless of which model fits your project." },
  { q: "Where is your team based?", a: "We're an MSME-registered IT company headquartered in Delhi NCR, following the same 7-stage delivery process across every engagement." },
];

const fadeUp = { hidden: { opacity: 0, y: 18 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }) };
const fadeUpRotate = { hidden: { opacity: 0, y: 18, rotate: 0 }, show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }) };

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return <MotionTag className={className} custom={i} variants={fadeUp} initial={reduceMotion ? false : "hidden"} whileInView="show" viewport={{ once: true, amount: 0.2 }} {...rest}>{children}</MotionTag>;
}

const MobilePerformanceSecurity = () => {
  const navigate = useNavigate();
  const { setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
  const [openFaq, setOpenFaq] = useState(-1);
  const [contactErrors, setContactErrors] = useState({});
  const [contactStatus, setContactStatus] = useState("idle");
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.title = "Mobile App Performance & Security | InQmise";
    const description = document.createElement("meta");
    description.name = "description"; description.content = facetBlurb.slice(0, 160);
    document.head.appendChild(description);
    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
    canonical.setAttribute("href", "https://inqmisetechnologies.com/mobile-performance-security");
    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "Service", "name": "Mobile App Performance & Security", "description": facetBlurb,
      "url": "https://inqmisetechnologies.com/mobile-performance-security",
      "provider": { "@id": "https://inqmisetechnologies.com/#organization" },
      "areaServed": ["IN", "US", "GB", "CA", "AU", "AE", "SG"], "serviceType": "Mobile App Performance & Security",
    });
    document.head.appendChild(schemaScript);
    const breadcrumbScript = document.createElement("script");
    breadcrumbScript.type = "application/ld+json";
    breadcrumbScript.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://inqmisetechnologies.com" },
        { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://inqmisetechnologies.com/services" },
        { "@type": "ListItem", "position": 3, "name": parent.title, "item": `https://inqmisetechnologies.com${parent.path}` },
        { "@type": "ListItem", "position": 4, "name": "Performance & Security", "item": "https://inqmisetechnologies.com/mobile-performance-security" },
      ],
    });
    document.head.appendChild(breadcrumbScript);
    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": FAQS.map((item) => ({ "@type": "Question", "name": item.q, "acceptedAnswer": { "@type": "Answer", "text": item.a } })) });
    document.head.appendChild(faqScript);
  }, []);

  const validateContactField = (name, value) => {
    switch (name) {
      case "fullname": return value.trim() ? "" : "Full name is required.";
      case "phone": if (!value.trim()) return ""; return isValidPhone(value) ? "" : "Enter a valid phone number.";
      case "email": if (!value.trim()) return "Email is required."; return emailRegex.test(value.toLowerCase()) ? "" : "Enter a valid email address.";
      case "subject": return value.trim() ? "" : "Subject is required.";
      case "description": return value.trim() ? "" : "Tell us a little about your project.";
      default: return "";
    }
  };
  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setContactErrors((prev) => ({ ...prev, [name]: validateContactField(name, value) }));
  };
  const handleContactSubmit = (e) => {
    e.preventDefault();
    const newErrors = { fullname: validateContactField("fullname", formData.fullname), phone: validateContactField("phone", formData.phone), email: validateContactField("email", formData.email), subject: validateContactField("subject", formData.subject), description: validateContactField("description", formData.description) };
    if (Object.values(newErrors).some(Boolean)) { setContactErrors(newErrors); return; }
    setContactStatus("submitting");
    submitLead(formData, "page")
      .then(() => { setContactStatus("success"); setFormData({ fullname: "", phone: "", email: "", subject: "", description: "" }); setContactErrors({}); })
      .catch(() => setContactStatus("error"));
  };

  return (
    <main className="editorial-ai pt-20">
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <a className="ea-text-link" style={{ display: "inline-block", marginBottom: 14 }} href={parent.path} onClick={(e) => { e.preventDefault(); navigate(parent.path); }}>← {parent.title}</a>
          <p className="ea-eyebrow">Part of {parent.title}</p>
          <h1>Mobile app performance &amp; security</h1>
          <p className="ea-lede">{facetBlurb}</p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={() => setIsOpen(true)} aria-label="Open contact form to start your project">Start your project ↗</button>
            <a className="ea-text-link" href={parent.path} onClick={(e) => { e.preventDefault(); navigate(parent.path); }}>View {parent.title} overview</a>
          </div>
        </Reveal>
        <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
          <div className="ea-engine-top"><span /> INQMISE / MOBILE SECURITY <b>LIVE</b></div>
          <h2><LuShieldCheck aria-hidden="true" style={{ verticalAlign: "-4px", marginRight: 8 }} />{parent.description[4]}</h2>
          <div className="ea-query"><small>PROCESS</small>{parent.description[2]}</div>
          <div className="ea-query"><small>PERFORM</small>{parent.description[1]}</div>
          <div className="ea-flow"><b>Build</b><i>→</i><b>Secure</b><i>→</i><b>Test</b></div>
        </Reveal>
      </header>

      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div><div className="ea-eyebrow">Why this facet</div><h2>Fast and secure<br />aren't a trade-off.</h2></div>
          <p>Both are built in from the start, not addressed after a problem shows up.</p>
        </Reveal>
        {FEATURE_ROWS.map((item, i) => (
          <Reveal as="article" i={i + 1} reduceMotion={reduceMotion} className="ea-feature" key={item.title}>
            <div className="ea-num">{item.num}</div><h3>{item.title}</h3><p>{item.copy}</p>
            <div className="ea-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
          </Reveal>
        ))}
      </section>

      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div><div className="ea-eyebrow">Our process</div><h2>Seven stages,<br />load and security tested.</h2></div>
            <p>The same disciplined process behind every service we offer.</p>
          </Reveal>
          <div className="aa-process-line">
            {PROCESS_STEPS.map((step, i) => (
              <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="aa-process-row" key={step.n}>
                <div className="aa-process-copy"><h3>{step.t}</h3><p>{step.d}</p></div>
                <div className="aa-process-node">{step.n}</div><div />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="ea-models">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div><div className="ea-eyebrow">What we build with</div><h2>Real stack,<br />real numbers.</h2></div>
            <p>The same mobile stack shown on /technologies, and the facts behind the team.</p>
          </Reveal>
          <div className="ea-model-layout">
            <Reveal as="div" className="ea-model-panel">
              <h3>Our mobile stack</h3>
              <div className="ea-tags" style={{ padding: "8px 0 0" }}>{mobileTools.map((toolName) => <span key={toolName} style={{ color: "var(--pine)" }}>{toolName}</span>)}</div>
              <h3 style={{ marginTop: 20 }}>Our back-end stack</h3>
              <div className="ea-tags" style={{ padding: "8px 0 0" }}>{backEndTools.map((toolName) => <span key={toolName} style={{ color: "var(--pine)" }}>{toolName}</span>)}</div>
            </Reveal>
            <div className="ea-metrics">{FACTS.map((m, i) => (<Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-metric" key={m.a}><strong>{m.a}</strong><span>{m.b}</span></Reveal>))}</div>
          </div>
        </div>
      </section>

      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div><div className="ea-eyebrow">Why choose us</div><h2>What sets our<br />team apart.</h2></div>
          <p>The same standards behind every service we deliver.</p>
        </Reveal>
        <div className="ea-cards">
          {chooseus.map((item, i) => (
            <Reveal as="article" i={(i % 4) + 1} reduceMotion={reduceMotion} className="ea-card" key={item.title}>
              <div className="ea-icon"><item.icon aria-hidden="true" /></div>
              <div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="ea-faq-section">
        <div className="ea-wrap ea-faq ea-section">
          <Reveal as="div" className="ea-section-head"><div className="ea-eyebrow">Common questions</div><h2>Mobile app performance and security FAQs</h2><p>Short answers to the questions we hear most.</p></Reveal>
          {FAQS.map((item, i) => {
            const isOpen = openFaq === i; const panelId = `mps-faq-panel-${i}`; const buttonId = `mps-faq-button-${i}`;
            return (
              <div className="ea-faq-row" key={item.q}>
                <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
                  <button id={buttonId} aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpenFaq(isOpen ? -1 : i)}>{item.q}<span>{isOpen ? "−" : "+"}</span></button>
                </h3>
                <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div><div className="ea-eyebrow">Explore more</div><h2>Related to<br />this facet.</h2></div>
          <p>Every facet is part of a real, dedicated service.</p>
        </Reveal>
        <div className="cs-tags-row">
          {RELATED.map((f) => (<a key={f.path} className="cs-tag" href={f.path} style={{ cursor: "pointer", textDecoration: "none" }} onClick={(e) => { e.preventDefault(); navigate(f.path); }}>{f.title}</a>))}
          <a className="cs-tag" href={parent.path} style={{ cursor: "pointer", textDecoration: "none", fontWeight: 700 }} onClick={(e) => { e.preventDefault(); navigate(parent.path); }}>{parent.title} →</a>
          <a className="cs-tag" href="/services" style={{ cursor: "pointer", textDecoration: "none" }} onClick={(e) => { e.preventDefault(); navigate("/services"); }}>All Services</a>
          <a className="cs-tag" href="/ai-solutions" style={{ cursor: "pointer", textDecoration: "none" }} onClick={(e) => { e.preventDefault(); navigate("/ai-solutions"); }}>AI Solutions</a>
        </div>
      </section>

      <section className="ea-cta" aria-labelledby="mps-contact-heading">
        <div className="ea-wrap ea-cta-grid">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <div className="ea-eyebrow">A practical first step</div><h2 id="mps-contact-heading">Ready to harden your app?</h2>
            <p>Tell us what you're building and we'll help you scope the right approach.</p>
          </Reveal>
          <Reveal as="div" i={1} reduceMotion={reduceMotion} className="ea-contact-card">
            <span>READY WHEN YOU ARE</span><h3>Start your project.</h3>
            <p>Tell us what you're building and we'll get back to you within one business day.</p>
            <form onSubmit={handleContactSubmit} aria-label="Performance & Security project inquiry form" className="ea-form" noValidate>
              {contactStatus === "success" && <p role="status" aria-live="polite" className="ea-form-msg success">Thanks — we've received your details and will be in touch shortly.</p>}
              {contactStatus === "error" && <p role="alert" aria-live="assertive" className="ea-form-msg error">Something went wrong submitting your request. Please try again.</p>}
              <div className="ea-field">
                <label htmlFor="mps-fullname">Full Name</label>
                <input id="mps-fullname" name="fullname" type="text" value={formData.fullname} onChange={handleContactChange} aria-invalid={Boolean(contactErrors.fullname)} aria-describedby={contactErrors.fullname ? "mps-fullname-error" : undefined} />
                {contactErrors.fullname && <p id="mps-fullname-error" className="ea-field-error">{contactErrors.fullname}</p>}
              </div>
              <div className="ea-form-row">
                <div className="ea-field">
                  <label htmlFor="mps-phone">Phone Number (optional)</label>
                  <input id="mps-phone" name="phone" type="tel" value={formData.phone} onChange={handleContactChange} aria-invalid={Boolean(contactErrors.phone)} aria-describedby={contactErrors.phone ? "mps-phone-error" : undefined} />
                  {contactErrors.phone && <p id="mps-phone-error" className="ea-field-error">{contactErrors.phone}</p>}
                </div>
                <div className="ea-field">
                  <label htmlFor="mps-email">Email</label>
                  <input id="mps-email" name="email" type="email" value={formData.email} onChange={handleContactChange} aria-invalid={Boolean(contactErrors.email)} aria-describedby={contactErrors.email ? "mps-email-error" : undefined} />
                  {contactErrors.email && <p id="mps-email-error" className="ea-field-error">{contactErrors.email}</p>}
                </div>
              </div>
              <div className="ea-field">
                <label htmlFor="mps-subject">Subject</label>
                <input id="mps-subject" name="subject" type="text" placeholder="e.g., Hardening our app before launch" value={formData.subject} onChange={handleContactChange} aria-invalid={Boolean(contactErrors.subject)} aria-describedby={contactErrors.subject ? "mps-subject-error" : undefined} />
                {contactErrors.subject && <p id="mps-subject-error" className="ea-field-error">{contactErrors.subject}</p>}
              </div>
              <div className="ea-field">
                <label htmlFor="mps-description">Project Details</label>
                <textarea id="mps-description" name="description" rows={3} value={formData.description} onChange={handleContactChange} aria-invalid={Boolean(contactErrors.description)} aria-describedby={contactErrors.description ? "mps-description-error" : undefined} />
                {contactErrors.description && <p id="mps-description-error" className="ea-field-error">{contactErrors.description}</p>}
              </div>
              <button type="submit" disabled={contactStatus === "submitting"} className="ea-button ea-primary ea-submit">{contactStatus === "submitting" ? "Submitting…" : "Start a conversation ↗"}</button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default MobilePerformanceSecurity;
